import {useEffect, useState} from 'react';
import {
  EventSummaryInternalDto,
  useEventDetailsStore,
  useEventListStore,
} from '../../../common';
import {apiRequest, paginatedApiRequest} from '../../../common/services/api';
import {
  deleteEventEndpoint,
  getEventListEndpoint,
} from '../../../common/constants/endpoints';
import {useOrganizerStore} from '../../../common/stores/useOrganizerStore';
import {EventSummaryCard} from '../../card/eventSummaryCard';
import {EventSummaryDto} from '../../../generated/models/EventSummaryDto';
import {useUiStore} from '../../../common/stores/useUiStore';
import {SearchInput} from '../../search/SearchInput';
import {useLocation, useNavigate} from 'react-router-dom';
import routes from '../../routes/routes';
import {AddEventButton} from '../../button/AddEventButton';
import {mapEventSummaryDto} from '../../../utils/mappers';

const fetchAllEvents = async (organizerId: string) => {
  return paginatedApiRequest<EventSummaryInternalDto[]>({
    endpoint: getEventListEndpoint(organizerId),
    method: 'POST',
    queryParams: {
      pageNumber: 1,
      pageSize: 10000,
    },
  });
};

export default function EventListScreen() {
  const setIsLoading = useUiStore(state => state.setIsLoading);
  const {eventList, setEventList, setSelectedEventId} = useEventListStore();
  const {setSelectedEventDetailsId} = useEventDetailsStore();
  const navigate = useNavigate();
  const location = useLocation();
  const searchTextParam = location.state?.searchText;

  const [searchText, setSearchText] = useState<string | null>(null);
  const organizerId = useOrganizerStore(state => state.organizer?.id);
  const [filteredEvents, setFilteredEvents] = useState<
    EventSummaryDto[] | null
  >(null);

  useEffect(() => {
    if (!searchText) {
      setFilteredEvents(eventList);
      return;
    }
    setFilteredEvents(
      eventList?.filter(event =>
        event.name.toLocaleLowerCase().includes(searchText.toLocaleLowerCase()),
      ) || [],
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText, eventList]);

  const handleOnDeleteEvent = async (eventId: string) => {
    await apiRequest<string>({
      endpoint: deleteEventEndpoint(organizerId || '', eventId),
      method: 'DELETE',
    });

    setIsLoading(true);
    const eventsResponse = await fetchAllEvents(organizerId || '');

    setIsLoading(false);

    if (!eventsResponse?.data) {
      return;
    }

    const eventsListResponse = eventsResponse?.data?.map(item =>
      mapEventSummaryDto(item),
    );
    setEventList(eventsListResponse);
    setFilteredEvents(eventsListResponse);
  };

  useEffect(() => {
    const getEventList = async () => {
      setIsLoading(true);
      const response = await fetchAllEvents(organizerId || '');

      setIsLoading(false);

      if (!response?.data) {
        return;
      }

      setEventList(response?.data?.map(item => mapEventSummaryDto(item)));
      setSearchText(searchTextParam);
      window.scroll(0, location.state?.scrollPosition || 0);
    };

    getEventList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOnCardClick = (eventId: string) => {
    setSelectedEventDetailsId(eventId);
    navigate(routes.getEvent, {
      state: {
        scrollPosition: window.scrollY,
        searchText,
      },
    });
  };

  const handleOnCardEdit = (eventId: string) => {
    setSelectedEventId(eventId);
    navigate(routes.editEvent, {
      state: {
        scrollPosition: window.scrollY,
        searchText,
      },
    });
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        minHeight: '100vh',
        gap: 30,
        padding: '20px',
        boxSizing: 'border-box',
      }}>
      <h1 style={{color: 'white'}}>All Events</h1>
      <div style={{marginBottom: 50, width: '100%', maxWidth: 600}}>
        <SearchInput
          onSearch={setSearchText}
          debounceMs={0}
          searchValue={searchTextParam}
        />
      </div>
      {filteredEvents?.length === 0 && (
        <p style={{color: '#666', textAlign: 'center'}}>No results found</p>
      )}
      {filteredEvents?.map(event => (
        <EventSummaryCard
          key={event.id}
          event={event}
          onDeleteEvent={handleOnDeleteEvent}
          onClick={handleOnCardClick}
          onEdit={handleOnCardEdit}
        />
      ))}
      <AddEventButton />
    </div>
  );
}
